import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, PhoneCall, CalendarCheck, Sparkles, ShieldCheck,
  Home as HomeIcon, MessageSquare, Mic, Zap, BarChart3, Users, Clock, Lock,
} from 'lucide-react';
import { features, images } from '../mock';

const iconMap = { PhoneCall, CalendarCheck, Sparkles, ShieldCheck, Home: HomeIcon, MessageSquare };

const EXTRA_FEATURES = [
  { icon: Mic, title: 'Natural Voice', desc: 'Choose from lifelike voices that sound like a friendly, professional assistant — not a robot.' },
  { icon: Zap, title: 'Instant Extract', desc: 'Paste any listing URL and pull photos, price, beds/baths and details in seconds.' },
  { icon: BarChart3, title: 'Call Analytics', desc: 'See caller intent, sentiment and outcome for every conversation on a clean dashboard.' },
  { icon: Users, title: 'Team Ready', desc: 'Invite team members, share leads, and keep everyone in sync on every conversation.' },
  { icon: Clock, title: 'Weekend Coverage', desc: 'Weekends and evenings are prime buyer hours. Your AiGENT never clocks out.' },
  { icon: Lock, title: 'Encrypted End-to-End', desc: 'Every call and transcript is encrypted in transit and at rest.' },
];

function FeaturesHero() {
  return (
    <section className="bg-brand-green text-brand-cream py-20 md:py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url(${images.interior2})` }} />
      <div className="absolute inset-0 bg-gradient-to-b from-brand-green/60 to-brand-green" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">Features</span>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-tight max-w-3xl">
          Everything an AI assistant should do for a modern realtor.
        </h1>
        <p className="mt-6 text-lg text-brand-cream/80 max-w-2xl">
          LISTING AiGENT combines call handling, listing intelligence, scheduling, and lead qualification in one calm, elegant experience.
        </p>
      </div>
    </section>
  );
}

function CoreFeatureCard({ feature }) {
  const Icon = iconMap[feature.icon] || Sparkles;
  return (
    <div className="p-8 rounded-2xl bg-white border border-brand-cream-dark hover:border-brand-green/30 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
      <div className="w-12 h-12 rounded-xl bg-brand-green flex items-center justify-center mb-6">
        <Icon className="w-5 h-5 text-brand-cream" strokeWidth={2} />
      </div>
      <h3 className="font-serif text-2xl text-brand-green mb-3">{feature.title}</h3>
      <p className="text-brand-green/70 leading-relaxed">{feature.desc}</p>
    </div>
  );
}

function CoreFeaturesSection() {
  return (
    <section className="py-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <h2 className="font-serif text-3xl md:text-4xl text-brand-green mb-12">Core capabilities</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f) => (<CoreFeatureCard key={f.title} feature={f} />))}
        </div>
      </div>
    </section>
  );
}

function ExtraFeatureItem({ icon: Icon, title, desc }) {
  return (
    <div className="flex gap-5">
      <div className="w-11 h-11 rounded-lg bg-brand-green/10 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-brand-green" strokeWidth={2} />
      </div>
      <div>
        <h3 className="font-serif text-xl text-brand-green mb-2">{title}</h3>
        <p className="text-brand-green/70 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function ExtraFeaturesSection() {
  return (
    <section className="py-24 bg-brand-cream-dark/40">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <h2 className="font-serif text-3xl md:text-4xl text-brand-green mb-12">And a lot more</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {EXTRA_FEATURES.map((f) => (<ExtraFeatureItem key={f.title} {...f} />))}
        </div>
      </div>
    </section>
  );
}

function FeaturesCTASection() {
  return (
    <section className="py-24">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
        <h2 className="font-serif text-4xl md:text-5xl text-brand-green">See it work for your listings.</h2>
        <p className="mt-5 text-brand-green/70 text-lg">Activate for $10 and get 7 days of full access. Upgrade or walk away.</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/download" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-lg bg-brand-green text-brand-cream font-semibold hover:scale-[1.02] transition-transform">
            Start 7-day trial <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-lg border border-brand-green/30 text-brand-green hover:bg-brand-green/5 transition-colors">
            Contact us
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Features() {
  return (
    <div className="bg-brand-cream">
      <FeaturesHero />
      <CoreFeaturesSection />
      <ExtraFeaturesSection />
      <FeaturesCTASection />
    </div>
  );
}
